import React from 'react';
import { Helmet } from 'react-helmet-async';
import PackageCard from '../components/services/PackageCard';
import FeatureBox from '../components/ui/FeatureBox';
import ProcessStep from '../components/ui/ProcessStep';
import CTASection from '../components/ui/CTASection';
import FAQ from '../components/ui/FAQ';
import { paidAdsContent } from '../content/paidAds';
import './PaidAds.css';

const PaidAds = () => {
  return (
    <>
      <Helmet>
        <title>الإعلانات الممولة - نمو ماركتينج</title>
        <meta
          name="description"
          content="نُطلق لك حملات إعلانية فعّالة ومدروسة تستهدف جمهورك المثالي وتحقق أفضل عائد على استثمارك"
        />
      </Helmet>

      <div className="paid-ads-page">
        {/* Hero Section */}
        <section className="page-hero">
          <div className="container">
            <div className="page-hero-content">
              <h1 className="page-hero-title">{paidAdsContent.hero.title}</h1>
              <p className="page-hero-subtitle">{paidAdsContent.hero.subtitle}</p>
            </div>
          </div>
        </section>

        {/* Why Paid Ads */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.whyNeed.title}</h2>
              <p className="section-subtitle">{paidAdsContent.whyNeed.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {paidAdsContent.whyNeed.reasons.map((reason) => (
                <FeatureBox
                  key={reason.id}
                  icon={reason.icon}
                  title={reason.title}
                  description={reason.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.platforms.title}</h2>
              <p className="section-subtitle">{paidAdsContent.platforms.subtitle}</p>
            </div>
            <div className="grid grid-3">
              {paidAdsContent.platforms.items.map((platform) => (
                <FeatureBox
                  key={platform.id}
                  icon={platform.icon}
                  title={platform.title}
                  description={platform.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.whatWeOffer.title}</h2>
            </div>
            <div className="grid grid-4">
              {paidAdsContent.whatWeOffer.features.map((feature) => (
                <FeatureBox
                  key={feature.id}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Performance Metrics */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.performanceMetrics.title}</h2>
              <p className="section-subtitle">{paidAdsContent.performanceMetrics.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {paidAdsContent.performanceMetrics.metrics.map((metric) => (
                <FeatureBox
                  key={metric.id}
                  icon={metric.icon}
                  title={metric.title}
                  description={metric.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section className="section portfolio-section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.portfolio.title}</h2>
              <p className="section-subtitle">{paidAdsContent.portfolio.subtitle}</p>
            </div>
            <div className="portfolio-grid">
              {paidAdsContent.portfolio.projects.map((project) => (
                <div key={project.id} className="portfolio-item">
                  <div className="portfolio-item-image">
                    <img src={project.image} alt={project.title} />
                    <div className="portfolio-item-overlay">
                      <div className="portfolio-item-overlay-content">
                        <span className="portfolio-item-category">{project.platform}</span>
                        <h3 className="portfolio-item-title">{project.title}</h3>
                        <p className="portfolio-item-description">{project.description}</p>
                        <div className="portfolio-item-features">
                          {project.metrics.map((metric, index) => (
                            <span key={index} className="portfolio-feature-tag">{metric}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Difference */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.ourDifference.title}</h2>
              <p className="section-subtitle">{paidAdsContent.ourDifference.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {paidAdsContent.ourDifference.differences.map((difference) => (
                <FeatureBox
                  key={difference.id}
                  icon={difference.icon}
                  title={difference.title}
                  description={difference.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="section packages-section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.packagesSection.title}</h2>
              <p className="section-subtitle">{paidAdsContent.packagesSection.subtitle}</p>
              {paidAdsContent.packagesSection.note && (
                <p className="packages-note">{paidAdsContent.packagesSection.note}</p>
              )}
            </div>
            <div className="packages-grid">
              {paidAdsContent.packages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  title={pkg.title}
                  subtitle={pkg.subtitle}
                  features={pkg.features}
                  badge={pkg.badge}
                  isFeatured={pkg.isFeatured}
                  whatsappMessage={pkg.whatsappMessage}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Campaign Types */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.campaignTypes.title}</h2>
              <p className="section-subtitle">{paidAdsContent.campaignTypes.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {paidAdsContent.campaignTypes.types.map((type) => (
                <FeatureBox
                  key={type.id}
                  icon={type.icon}
                  title={type.title}
                  description={type.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.process.title}</h2>
            </div>
            <div className="process-grid">
              {paidAdsContent.process.steps.map((step) => (
                <ProcessStep
                  key={step.id}
                  number={step.number}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Guarantees */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{paidAdsContent.guarantees.title}</h2>
            </div>
            <div className="grid grid-4">
              {paidAdsContent.guarantees.items.map((item) => (
                <FeatureBox
                  key={item.id}
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ title={paidAdsContent.faq.title} items={paidAdsContent.faq.items} />

        {/* Final CTA */}
        <CTASection
          title={paidAdsContent.finalCTA.title}
          subtitle={paidAdsContent.finalCTA.subtitle}
          buttonText={paidAdsContent.finalCTA.buttonText}
          backgroundStyle="gradient"
        />
      </div>
    </>
  );
};

export default PaidAds;
